import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableShell } from "@/components/EntityManager";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, FileText, Download, Save, Wand2, MessageCircle, Mail, Pencil, Trash2, Columns2, Plus, FileCheck2, Check } from "lucide-react";
import { inr, fmtDate } from "@/lib/format";
import { printOfficeStatement, printOfficeLedgerT, printLoaRegister } from "@/lib/print";
import { HEAD_OFFICE, COMPANY } from "@/lib/company";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const OFFICES = ["Chennai", "Tada", "Hyderabad", "Jaipur"];

export default function LetterOfAccount() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [office, setOffice] = useState("Chennai");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loaSearch, setLoaSearch] = useState("");
  const [loaRegister, setLoaRegister] = useState(null);
  const [loaRegisterOpen, setLoaRegisterOpen] = useState(false);

  const openLoaRegister = async () => {
    try {
      const r = await api.get("/loa");
      setLoaRegister(r.data.rows || []);
      setLoaRegisterOpen(true);
    } catch {
      toast.error("Could not load LOA register");
    }
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [issuedLoa, setIssuedLoa] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ date: new Date().toISOString().slice(0, 10), side: "debit", category: "", particulars: "", amount: "" });

  const MANUAL_CATEGORIES = ["Food", "Travelling Expenses", "Staff Conveyance", "Office Expenses", "Cash Remittance", "Other"];

  const saveManual = async () => {
    try {
      await api.post("/statement-entry", { office, ...manualForm, category: manualForm.category === "Other" ? "" : manualForm.category });
      toast.success(`${manualForm.side === "credit" ? "Credit" : "Debit"} entry added`);
      setManualOpen(false);
      setManualForm({ date: new Date().toISOString().slice(0, 10), side: "debit", category: "", particulars: "", amount: "" });
      generate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveOpening = async () => {
    try {
      await api.post("/admin/office-opening", { office, opening_balance: Number(opening) || 0 });
      toast.success(`Opening balance saved for ${office}`);
      generate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const backfill = async () => {
    const scoped = fromDate || toDate;
    const scopeMsg = scoped
      ? `dated ${fromDate || "beginning"} to ${toDate || "today"}`
      : "with no date restriction (ALL untagged)";
    if (!window.confirm(`Tag untagged bills/receipts/consignments (no office) ${scopeMsg} to "${office}"? This updates old records.`)) return;
    try {
      const params = new URLSearchParams({ office });
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      const r = await api.post(`/admin/backfill-office?${params.toString()}`);
      toast.success(`Tagged: ${r.data.invoices} bills, ${r.data.receipts} receipts, ${r.data.shipments} consignments → ${office}`);
      generate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const rowKey = (r) => `${r.source}-${r.id}`;
  const printRows = () => (sel.size ? data.rows.filter((r) => sel.has(rowKey(r))) : data.rows);
  const printData = () => {
    if (!sel.size) return data;
    const rows = printRows();
    return {
      ...data,
      rows,
      total_debit: rows.reduce((s, r) => s + (r.debit || 0), 0),
      total_credit: rows.reduce((s, r) => s + (r.credit || 0), 0),
      total_hire: rows.reduce((s, r) => s + (r.vehicle_hire || 0), 0),
      total_advance: rows.reduce((s, r) => s + (r.vehicle_advance || 0), 0),
      total_vbalance: rows.reduce((s, r) => s + (r.vehicle_balance || 0), 0),
      total_expenses: rows.reduce((s, r) => s + (r.expenses || 0), 0),
    };
  };

  const exportCsv = () => {
    if (!data) return;
    const header = "Branch,Particulars,Ref No.,Date,Debit (Bill),Vehicle Hire,Vehicle Advance,Vehicle Balance,Debit Expenses,Credit (Received),Balance Amount";
    const d = printData();
    const lines = [
      header,
      `"","Opening Balance",,,,,,,,,${data.opening}`,
      ...d.rows.map((r) => [data.office, r.particulars, refNo(r), r.date, r.debit || "", r.vehicle_hire || "", r.vehicle_advance || "", r.vehicle_balance || "", r.expenses || "", r.credit || "", r.balance].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")),
      `"","TOTAL",,,${d.total_debit},${d.total_hire || 0},${d.total_advance || 0},${d.total_vbalance || 0},${d.total_expenses || 0},${d.total_credit},${data.closing}`,
      `"","CBS — Closing Balance (${data.from_date || "Beginning"} to ${data.to_date || "Today"})",,,,,,,,,${data.closing}`,
    ].join("\n");
    const blob = new Blob([lines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `office-statement-${data.office}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Statement exported");
  };

  const generate = async (openLedger = false, ov = {}) => {
    setLoading(true);
    try {
      const params = { office: ov.office ?? office };
      const f = ov.from_date ?? fromDate;
      const t = ov.to_date ?? toDate;
      if (f) params.from_date = f;
      if (t) params.to_date = t;
      const res = await api.get("/reports/office-statement", { params });
      setData(res.data);
      setSel(new Set());
      try {
        const lk = await api.get("/loa/lookup", { params: { office: params.office, from_date: f || "", to_date: t || "" } });
        setIssuedLoa(lk.data?.loa_no ? lk.data : null);
      } catch {
        setIssuedLoa(null);
      }
      if (openLedger) setLedgerOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const reprintLoa = async (l) => {
    try {
      const params = { office: l.office || "Chennai" };
      if (l.from_date) params.from_date = l.from_date;
      if (l.to_date) params.to_date = l.to_date;
      const res = await api.get("/reports/office-statement", { params });
      let hist = null;
      try {
        hist = (await api.get("/reports/cbs-history?days=30")).data;
      } catch {
        hist = null;
      }
      printOfficeStatement(res.data, hist, l);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load that LOA's statement");
    }
  };

  const loaSummaryText = (l) => {
    const dmy = (d) => (d ? `${String(d).slice(8, 10)}-${String(d).slice(5, 7)}-${String(d).slice(0, 4)}` : "");
    return (
      `*NISHA TRANSPORT CORPORATION*\n*LETTER OF ACCOUNT — LOA No. ${l.loa_no}*\n` +
      `Date Issued: ${dmy(l.date)}\n` +
      `Branch: ${l.office}\n` +
      `Period: ${l.from_date ? dmy(l.from_date) : "Beginning"} to ${l.to_date ? dmy(l.to_date) : "Today"}\n` +
      `CBS (Closing Balance): ₹${Math.abs(Number(l.closing) || 0).toLocaleString("en-IN")}${(Number(l.closing) || 0) < 0 ? " (Credit)" : ""}\n` +
      `Issued By: ${l.created_by || "—"}`
    );
  };

  const loaWhatsApp = (l) => {
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(loaSummaryText(l))}`, "_blank");
  };

  const loaEmail = (l) => {
    const subject = `LOA No. ${l.loa_no} — ${l.office} Account Statement`;
    window.location.href = `mailto:${HEAD_OFFICE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(loaSummaryText(l).replace(/\*/g, ""))}`;
    toast.success("Opening your email app…", { description: `To: ${HEAD_OFFICE.email}` });
  };

  const findLoa = async (e) => {
    e.preventDefault();
    const q = loaSearch.trim();
    if (!q) return;
    try {
      const l = (await api.get(`/loa/${encodeURIComponent(q)}`)).data;
      setOffice(l.office || "Chennai");
      setFromDate(l.from_date || "");
      setToDate(l.to_date || "");
      toast.success(`LOA No. ${l.loa_no} — ${l.office}, issued ${l.date}`);
      generate(false, { office: l.office || "Chennai", from_date: l.from_date || "", to_date: l.to_date || "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "LOA number not found");
    }
  };

  const startEdit = (r) => {
    setEditForm(
      r.source === "challan"
        ? { date: r.date || "", vehicle_hire: r.vehicle_hire || "", vehicle_advance: r.vehicle_advance || "", vehicle_balance: r.vehicle_balance || "" }
        : r.source === "manual"
          ? { date: r.date || "", category: r.category || "", particulars: r.raw_particulars || r.particulars || "", amount: r.expenses || r.credit || "" }
          : { date: r.date || "", amount: r.debit || r.credit || r.expenses || "" }
    );
    setEditRow(r);
  };

  const saveEdit = async () => {
    try {
      await api.put("/statement-entry", { source: editRow.source, id: editRow.id, ...editForm });
      toast.success("Entry updated");
      setEditRow(null);
      generate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const delRow = async (r) => {
    if (!window.confirm(`Delete this ${r.type} entry (${r.particulars})? This removes the underlying record permanently.`)) return;
    try {
      await api.delete(`/statement-entry/${r.source}/${r.id}`);
      toast.success("Entry deleted");
      generate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const inr0 = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
  const statementSummary = () => {
    const period = `${data.from_date || "Beginning"} to ${data.to_date || "Today"}`;
    return (
      `${COMPANY.name}\n` +
      `OFFICE ACCOUNT STATEMENT\n` +
      `Office: ${data.office}\n` +
      `Period: ${period}\n\n` +
      `Opening Balance: ${inr0(data.opening)}\n` +
      `Total Debit (Bills): ${inr0(data.total_debit)}\n` +
      `Total Credit (Received): ${inr0(data.total_credit)}\n` +
      `Closing Balance: ${inr0(data.closing)}\n\n` +
      `(Detailed statement PDF to follow.)`
    );
  };

  const sendWhatsApp = async () => {
    if (!data) return;
    try {
      toast.info("Preparing statement PDF…");
      const params = { office };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await api.get("/reports/office-statement/pdf", { params, responseType: "blob" });
      const fname = `statement-${office.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
      const file = new File([res.data], fname, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${office} Office Statement`, text: statementSummary() });
        return;
      }
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(statementSummary())}`, "_blank");
      toast.success("Statement PDF downloaded", {
        description: "Attach the downloaded PDF in the WhatsApp chat that just opened.",
      });
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(formatApiError(e.response?.data?.detail) || "Could not prepare the PDF");
    }
  };

  const sendCbsWhatsApp = () => {
    if (!data) return;
    const msg =
      `*NISHA TRANSPORT CORPORATION*\n*CBS — CLOSING BALANCE*\n` +
      `Branch: ${data.office}\n` +
      `Period: ${data.from_date ? fmtDate(data.from_date) : "Beginning"} to ${data.to_date ? fmtDate(data.to_date) : "Today"}\n` +
      `CBS: ₹${Math.abs(Number(data.closing) || 0).toLocaleString("en-IN")}${(Number(data.closing) || 0) < 0 ? " (Credit)" : ""}`;
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const sendEmail = () => {
    if (!data) return;
    const subject = `${data.office} Office Account Statement — ${data.from_date || "Beginning"} to ${data.to_date || "Today"}`;
    const url = `mailto:${HEAD_OFFICE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(statementSummary())}`;
    window.location.href = url;
    toast.success("Opening your email app…", {
      description: `To: ${HEAD_OFFICE.email}. Attach the printed PDF if needed.`,
    });
  };

  const allSel = data?.rows?.length > 0 && data.rows.every((r) => sel.has(rowKey(r)));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(data.rows.map(rowKey)));
  const toggleOne = (r) =>
    setSel((prev) => {
      const n = new Set(prev);
      const k = rowKey(r);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const debitSide = data ? data.rows.filter((r) => (r.effect || 0) > 0) : [];
  const creditSide = data ? data.rows.filter((r) => (r.credit || 0) > 0) : [];
  const totalDr = debitSide.reduce((s, r) => s + (r.effect || 0), 0) + (data?.opening > 0 ? data.opening : 0);
  const totalCr = creditSide.reduce((s, r) => s + (r.credit || 0), 0) + (data?.opening < 0 ? -data.opening : 0);

  const categoryTotals = () => {
    const m = {};
    (data?.rows || []).forEach((r) => {
      if (r.source !== "manual") return;
      const c = r.category || "Other";
      const g = m[c] || (m[c] = { category: c, debit: 0, credit: 0 });
      g.debit += r.expenses || 0;
      g.credit += r.credit || 0;
    });
    return Object.values(m).sort((a, b) => b.debit + b.credit - (a.debit + a.credit));
  };

  const RIGHT_COLS = ["Debit (Bill)", "Vehicle Hire", "Vehicle Advance", "Vehicle Balance", "Debit Expenses", "Credit (Received)", "Balance Amount"];
  const refNo = (r) => String(r.ref || "").replace(/^INV[-\s]?/i, "");

  return (
    <div>
      <PageHeader
        title="Letter of Account"
        subtitle="Branch account statement (ledger) — all bills raised and payments received for a branch, with running balance."
      />

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="grid gap-1.5">
          <Label>Select Branch</Label>
          <Select value={office} onValueChange={setOffice}>
            <SelectTrigger className="w-[180px]" data-testid="statement-office">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OFFICES.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label>From Date</Label>
          <Input type="date" className="w-[170px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="statement-from" />
        </div>
        <div className="grid gap-1.5">
          <Label>To Date</Label>
          <Input type="date" className="w-[170px]" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="statement-to" />
        </div>
        <Button onClick={() => generate(true)} disabled={loading} data-testid="generate-statement-btn">
          <FileText className="mr-2 h-4 w-4" /> {loading ? "Loading…" : "Submit"}
        </Button>
        <Button
          className="bg-emerald-600 text-white hover:bg-emerald-700"
          disabled={!data}
          onClick={async () => {
            if (!data) return;
            try {
              const res = await api.post("/loa/issue", { office: data.office, from_date: data.from_date || "", to_date: data.to_date || "", closing: data.closing });
              setIssuedLoa(res.data);
              if (res.data.duplicate) {
                toast.warning(`LOA already generated — LOA No. ${res.data.loa_no} (issued ${fmtDate(res.data.date)})`, {
                  description: "Use Print Letter to reprint it. One LOA per branch & period.",
                });
              } else {
                toast.success(`LOA No. ${res.data.loa_no} generated for ${data.office} — ${data.from_date ? fmtDate(data.from_date) : "Beginning"} to ${data.to_date ? fmtDate(data.to_date) : "Today"}`);
              }
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail));
            }
          }}
          data-testid="loa-generate-btn"
        >
          <FileCheck2 className="mr-2 h-4 w-4" /> Generate
        </Button>
        <Button variant="outline" onClick={() => setManualOpen(true)} data-testid="add-manual-entry-btn">
          <Plus className="mr-2 h-4 w-4" /> Manual Entry
        </Button>
        <Button variant="outline" disabled={!data} onClick={() => setLedgerOpen(true)} data-testid="open-ledger-view-btn">
          <Columns2 className="mr-2 h-4 w-4" /> Debit / Credit View
        </Button>
        <Button
          variant="outline"
          disabled={!data}
          onClick={async () => {
            if (!data) return;
            let hist = null;
            let loa = null;
            try {
              hist = (await api.get("/reports/cbs-history?days=30")).data;
            } catch {
              hist = null;
            }
            try {
              loa = (await api.post("/loa/issue", { office: data.office, from_date: data.from_date || "", to_date: data.to_date || "", closing: data.closing })).data;
            } catch {
              loa = null;
            }
            printOfficeStatement(printData(), hist, loa);
          }}
          data-testid="print-statement-btn"
        >
          <Printer className="mr-2 h-4 w-4" /> Print Letter
        </Button>
        <Button variant="outline" disabled={!data} onClick={exportCsv} data-testid="export-statement-btn">
          <Download className="mr-2 h-4 w-4" /> Excel
        </Button>
        <form onSubmit={findLoa} className="flex items-center gap-1.5">
          <Input
            value={loaSearch}
            onChange={(e) => setLoaSearch(e.target.value)}
            placeholder="Find LOA No."
            className="h-9 w-32"
            data-testid="loa-search-input"
          />
          <Button type="submit" variant="outline" size="sm" className="h-9" data-testid="loa-search-btn">
            Find
          </Button>
        </form>
        <Button variant="outline" onClick={openLoaRegister} data-testid="loa-register-btn">
          LOA Register
        </Button>
        <Button variant="outline" className="gap-1.5 text-emerald-700" disabled={!data} onClick={sendWhatsApp} data-testid="whatsapp-statement-btn">
          <MessageCircle className="h-4 w-4" /> WhatsApp PDF
        </Button>
        <Button variant="outline" className="gap-1.5 text-sky-700" disabled={!data} onClick={sendCbsWhatsApp} data-testid="cbs-whatsapp-btn">
          <MessageCircle className="h-4 w-4" /> CBS WhatsApp
        </Button>
        <Button variant="outline" className="gap-1.5 text-blue-700" disabled={!data} onClick={sendEmail} data-testid="email-statement-btn">
          <Mail className="h-4 w-4" /> Email Head Office
        </Button>
      </div>

      {isAdmin && (
        <div className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">Opening Balance for {office} (₹)</Label>
            <Input type="number" className="w-[180px]" value={opening} onChange={(e) => setOpening(e.target.value)} placeholder="0" data-testid="opening-balance-input" />
          </div>
          <Button size="sm" variant="outline" onClick={saveOpening} data-testid="save-opening-btn">
            <Save className="mr-2 h-4 w-4" /> Save Opening
          </Button>
          <Button size="sm" variant="outline" onClick={backfill} data-testid="backfill-office-btn">
            <Wand2 className="mr-2 h-4 w-4" /> Tag untagged records to {office}
          </Button>
        </div>
      )}

      {!data ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 py-20 text-center">
          <FileText className="mb-3 h-10 w-10 text-slate-300" />
          <p className="text-muted-foreground">Choose an office and date range, then click Submit to view the Debit &amp; Credit details. After checking, click Generate to issue the LOA (one time per period).</p>
        </div>
      ) : (
        <TableShell>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2 font-display text-lg font-bold">
              {data.office} Office — Account Statement
              {issuedLoa?.loa_no && (
                <span className="rounded-full bg-emerald-600 px-3 py-1 font-sans text-xs font-bold text-white" data-testid="loa-issued-badge">
                  LOA No. {issuedLoa.loa_no} issued{issuedLoa.date ? ` · ${fmtDate(issuedLoa.date)}` : ""}
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {sel.size ? `${sel.size} selected · ` : ""}{data.from_date || "Beginning"} to {data.to_date || "Today"}
            </div>
          </div>
          <div className="border-b border-sky-100 bg-sky-50/60 px-4 py-3" data-testid="statement-cbs-bar">
            <table className="border-collapse text-sm" data-testid="statement-cbs-table">
              <thead>
                <tr>
                  <th className="border border-sky-300 bg-sky-100 px-5 py-1.5 text-left text-xs font-bold uppercase tracking-[0.12em] text-sky-900">Date From</th>
                  <th className="border border-sky-300 bg-sky-100 px-5 py-1.5 text-left text-xs font-bold uppercase tracking-[0.12em] text-sky-900">Date To</th>
                  <th className="border border-sky-300 bg-sky-100 px-5 py-1.5 text-right text-xs font-bold uppercase tracking-[0.12em] text-sky-900">CBS</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-sky-300 px-5 py-1.5 font-semibold text-sky-900" data-testid="statement-cbs-from">{data.from_date ? fmtDate(data.from_date) : "Beginning"}</td>
                  <td className="border border-sky-300 px-5 py-1.5 font-semibold text-sky-900" data-testid="statement-cbs-to">{data.to_date ? fmtDate(data.to_date) : "Today"}</td>
                  <td className="border border-sky-300 px-5 py-1.5 text-right font-display font-bold tabular-nums text-sky-900" data-testid="statement-cbs-value">{inr(data.closing)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="w-[36px]">
                  <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="statement-select-all" />
                </TableHead>
                {["Branch", "Particulars", "Ref No.", "Date", ...RIGHT_COLS, "Actions"].map((h) => (
                  <TableHead
                    key={h}
                    className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${RIGHT_COLS.includes(h) ? "text-right" : ""}`}
                  >
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-slate-50/60">
                <TableCell colSpan={11} className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Opening Balance
                </TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums">{inr(data.opening)}</TableCell>
                <TableCell />
              </TableRow>
              {data.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="py-10 text-center text-muted-foreground">No transactions in this period.</TableCell>
                </TableRow>
              )}
              {data.rows.map((r, i) => (
                <TableRow key={i} data-testid="statement-row" className={sel.has(rowKey(r)) ? "bg-primary/5" : ""}>
                  <TableCell>
                    <Checkbox checked={sel.has(rowKey(r))} onCheckedChange={() => toggleOne(r)} data-testid="statement-select-row" />
                  </TableCell>
                  <TableCell className="text-sm">{data.office}</TableCell>
                  <TableCell className="text-sm">{r.particulars}</TableCell>
                  <TableCell className="text-sm">{refNo(r) || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{r.debit ? inr(r.debit) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{r.vehicle_hire ? inr(r.vehicle_hire) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{r.vehicle_advance ? inr(r.vehicle_advance) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{r.vehicle_balance ? inr(r.vehicle_balance) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-amber-700">{r.expenses ? inr(r.expenses) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-emerald-600">{r.credit ? inr(r.credit) : "—"}</TableCell>
                  <TableCell className="text-right text-sm font-medium tabular-nums">{inr(r.balance)}</TableCell>
                  <TableCell>
                    {isAdmin && (
                      <span className="flex items-center gap-0.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(r)} data-testid="statement-edit-btn">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => delRow(r)} data-testid="statement-delete-btn">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-100 font-bold" data-testid="statement-total-row">
                <TableCell colSpan={5} className="text-right text-sm">TOTAL</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(data.total_debit)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(data.total_hire || 0)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(data.total_advance || 0)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(data.total_vbalance || 0)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(data.total_expenses || 0)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(data.total_credit)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-primary">{inr(data.closing)}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow className="bg-sky-50 font-bold hover:bg-sky-50" data-testid="statement-cbs-row">
                <TableCell colSpan={11} className="text-right text-sm text-sky-800">
                  CBS — CLOSING BALANCE ({data.from_date ? fmtDate(data.from_date) : "Beginning"} TO {data.to_date ? fmtDate(data.to_date) : "Today"})
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-sky-900">{inr(data.closing)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
          {categoryTotals().length > 0 && (
            <div className="border-t border-slate-100 px-4 py-3" data-testid="category-totals">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Manual Entry Totals by Category</div>
              <div className="flex flex-wrap gap-2">
                {categoryTotals().map((c) => (
                  <div key={c.category} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs" data-testid="category-total-chip">
                    <span className="font-semibold">{c.category}</span>
                    {c.debit > 0 && <span className="tabular-nums text-amber-700">Dr {inr(c.debit)}</span>}
                    {c.credit > 0 && <span className="tabular-nums text-emerald-700">Cr {inr(c.credit)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TableShell>
      )}

      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[1000px]" data-testid="ledger-t-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8 font-display">
              <span>{data?.office} Office — Debit / Credit Ledger</span>
              <Button size="sm" variant="outline" onClick={() => printOfficeLedgerT(data)} data-testid="ledger-t-print-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          {data && (
            <>
              <div className="grid grid-cols-2 overflow-hidden rounded-lg border-2 border-slate-900">
                <div className="border-r-2 border-slate-900">
                  <div className="bg-slate-900 py-1.5 text-center text-xs font-bold tracking-[0.2em] text-white">DEBIT (DR.)</div>
                  <table className="w-full text-xs">
                    <thead className="bg-muted"><tr className="text-left [&>th]:p-2"><th>Date</th><th>Particulars</th><th className="text-right">Amount</th></tr></thead>
                    <tbody data-testid="ledger-debit-side">
                      {data.opening > 0 && (
                        <tr className="border-t bg-slate-50 font-semibold [&>td]:p-2"><td>—</td><td>Opening Balance b/f</td><td className="text-right tabular-nums">{inr(data.opening)}</td></tr>
                      )}
                      {debitSide.map((r, i) => (
                        <tr key={i} className="border-t [&>td]:p-2" data-testid="ledger-debit-row">
                          <td className="whitespace-nowrap">{fmtDate(r.date)}</td>
                          <td>{r.particulars}{r.ref ? <span className="text-muted-foreground"> ({r.ref})</span> : ""}</td>
                          <td className="text-right tabular-nums">{inr(r.effect)}</td>
                        </tr>
                      ))}
                      {!debitSide.length && data.opening <= 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">No debit entries.</td></tr>}
                      <tr className="border-t bg-slate-100 font-bold [&>td]:p-2"><td colSpan={2} className="text-right">TOTAL DR.</td><td className="text-right tabular-nums" data-testid="ledger-total-dr">{inr(totalDr)}</td></tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className="bg-emerald-800 py-1.5 text-center text-xs font-bold tracking-[0.2em] text-white">CREDIT (CR.)</div>
                  <table className="w-full text-xs">
                    <thead className="bg-muted"><tr className="text-left [&>th]:p-2"><th>Date</th><th>Particulars</th><th className="text-right">Amount</th></tr></thead>
                    <tbody data-testid="ledger-credit-side">
                      {data.opening < 0 && (
                        <tr className="border-t bg-slate-50 font-semibold [&>td]:p-2"><td>—</td><td>Opening Balance b/f</td><td className="text-right tabular-nums">{inr(-data.opening)}</td></tr>
                      )}
                      {creditSide.map((r, i) => (
                        <tr key={i} className="border-t [&>td]:p-2" data-testid="ledger-credit-row">
                          <td className="whitespace-nowrap">{fmtDate(r.date)}</td>
                          <td>{r.particulars}{r.ref ? <span className="text-muted-foreground"> ({r.ref})</span> : ""}</td>
                          <td className="text-right tabular-nums text-emerald-700">{inr(r.credit)}</td>
                        </tr>
                      ))}
                      {!creditSide.length && data.opening >= 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">No credit entries.</td></tr>}
                      <tr className="border-t bg-slate-100 font-bold [&>td]:p-2"><td colSpan={2} className="text-right">TOTAL CR.</td><td className="text-right tabular-nums" data-testid="ledger-total-cr">{inr(totalCr)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-slate-100 px-4 py-2 text-sm font-bold" data-testid="ledger-closing">
                <span>CLOSING BALANCE (Dr. − Cr.)</span>
                <span className="tabular-nums">{inr(data.closing)}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="statement-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit {editRow?.type} Entry</DialogTitle>
          </DialogHeader>
          {editRow && (
            <div className="grid gap-3">
              <p className="text-xs text-muted-foreground">{editRow.particulars}{editRow.ref ? ` (${editRow.ref})` : ""}</p>
              <div className="grid gap-1.5">
                <Label>Date</Label>
                <Input type="date" value={editForm.date || ""} onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))} data-testid="statement-edit-date" />
              </div>
              {editRow.source === "challan" ? (
                <>
                  {[["vehicle_hire", "Vehicle Hire (₹)"], ["vehicle_advance", "Vehicle Advance (₹)"], ["vehicle_balance", "Vehicle Balance (₹)"]].map(([k, lbl]) => (
                    <div key={k} className="grid gap-1.5">
                      <Label>{lbl}</Label>
                      <Input type="number" value={editForm[k] ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`statement-edit-${k}`} />
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {editRow.source === "manual" && (
                    <>
                      <div className="grid gap-1.5">
                        <Label>Category</Label>
                        <Select value={editForm.category || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, category: v }))}>
                          <SelectTrigger data-testid="statement-edit-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {MANUAL_CATEGORIES.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-1.5">
                        <Label>Particulars</Label>
                        <Input value={editForm.particulars ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, particulars: e.target.value }))} data-testid="statement-edit-particulars" />
                      </div>
                    </>
                  )}
                  <div className="grid gap-1.5">
                    <Label>Amount (₹)</Label>
                    <Input type="number" value={editForm.amount ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))} data-testid="statement-edit-amount" />
                  </div>
                </>
              )}
              <Button onClick={saveEdit} data-testid="statement-edit-save-btn">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="manual-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add Manual Entry — {office}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Debit = branch expense (Debit Expenses column). Credit = amount the branch sends back (Credit column).</p>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={manualForm.side === "debit" ? "default" : "outline"}
                className={manualForm.side === "debit" ? "" : "text-muted-foreground"}
                onClick={() => setManualForm((f) => ({ ...f, side: "debit" }))}
                data-testid="manual-entry-side-debit"
              >
                Debit (Expense)
              </Button>
              <Button
                type="button"
                variant={manualForm.side === "credit" ? "default" : "outline"}
                className={manualForm.side === "credit" ? "bg-emerald-700 hover:bg-emerald-800" : "text-muted-foreground"}
                onClick={() => setManualForm((f) => ({ ...f, side: "credit", category: f.category || "Cash Remittance" }))}
                data-testid="manual-entry-side-credit"
              >
                Credit (Sent Back)
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Date</Label>
                <Input type="date" value={manualForm.date} onChange={(e) => setManualForm((f) => ({ ...f, date: e.target.value }))} data-testid="manual-entry-date" />
              </div>
              <div className="grid gap-1.5">
                <Label>Category</Label>
                <Select value={manualForm.category} onValueChange={(v) => setManualForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="manual-entry-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {MANUAL_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Particulars</Label>
              <Input placeholder="e.g. Staff monthly conveyance — Sept" value={manualForm.particulars} onChange={(e) => setManualForm((f) => ({ ...f, particulars: e.target.value }))} data-testid="manual-entry-particulars" />
            </div>
            <div className="grid gap-1.5">
              <Label>Amount (₹)</Label>
              <Input type="number" placeholder="0" value={manualForm.amount} onChange={(e) => setManualForm((f) => ({ ...f, amount: e.target.value }))} data-testid="manual-entry-amount" />
            </div>
            <Button
              onClick={saveManual}
              className={manualForm.side === "credit" ? "bg-emerald-700 hover:bg-emerald-800" : ""}
              disabled={!manualForm.particulars.trim() || !Number(manualForm.amount)}
              data-testid="manual-entry-save-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> Add {manualForm.side === "credit" ? "Credit" : "Debit"} Entry
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={loaRegisterOpen} onOpenChange={setLoaRegisterOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="loa-register-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">LOA Register ({(loaRegister || []).length})</DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                {["LOA No.", "Date", "Branch", "Period", "CBS", "Issued By", ""].map((h) => (
                  <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(loaRegister || []).length === 0 ? (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No LOAs issued yet.</TableCell></TableRow>
              ) : (
                (loaRegister || []).map((r) => {
                  const isCurrent = !!data && r.office === data.office && (r.from_date || "") === (data.from_date || "") && (r.to_date || "") === (data.to_date || "");
                  return (
                  <TableRow key={r.id} className={isCurrent ? "bg-emerald-50/70" : undefined} data-testid="loa-register-row">
                    <TableCell className="font-bold">
                      <span className="inline-flex items-center gap-1.5">
                        {r.loa_no}
                        {isCurrent && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white" title="Matches the statement currently on screen" data-testid="loa-register-issued-tick">
                            <Check className="h-3 w-3" /> Issued
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{r.date ? fmtDate(r.date) : "—"}</TableCell>
                    <TableCell className="text-sm">{r.office}</TableCell>
                    <TableCell className="text-sm">{(r.from_date ? fmtDate(r.from_date) : "Beginning") + " to " + (r.to_date ? fmtDate(r.to_date) : "Today")}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{inr(r.closing)}</TableCell>
                    <TableCell className="text-sm">{r.created_by || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-0.5">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title={`Reprint LOA No. ${r.loa_no}`} onClick={() => reprintLoa(r)} data-testid="reprint-loa-btn">
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="WhatsApp to Head Office" onClick={() => loaWhatsApp(r)} data-testid="loa-whatsapp-btn">
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700" title="Email to Head Office" onClick={() => loaEmail(r)} data-testid="loa-email-btn">
                          <Mail className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <div className="flex justify-end">
            <Button onClick={() => printLoaRegister(loaRegister || [])} data-testid="print-loa-register-btn">
              <Printer className="mr-2 h-4 w-4" /> Print Register
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <CbsHistoryCard />
    </div>
  );
}

const CBS_COLORS = ["#0ea5e9", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#14b8a6", "#f43f5e", "#64748b"];

function CbsHistoryCard() {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    api.get("/reports/cbs-history?days=30").then((r) => setHist(r.data)).catch(() => {});
  }, []);
  if (!hist?.branches?.length) return null;
  const chartData = hist.branches[0].points.map((p, i) => {
    const row = { date: fmtDate(p.date).slice(0, 6) };
    hist.branches.forEach((b) => {
      row[b.branch] = b.points[i]?.cbs ?? 0;
    });
    return row;
  });
  return (
    <div className="mt-6 rounded-lg border border-border bg-card p-4" data-testid="cbs-history-card">
      <h3 className="font-display text-lg font-bold">CBS History — Last 30 Days</h3>
      <p className="mb-3 text-xs text-muted-foreground">Branch-wise closing balance (CBS) movement, day by day.</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={28} />
            <YAxis tick={{ fontSize: 11 }} width={64} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`)} />
            <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
            <Legend />
            {hist.branches.map((b, i) => (
              <Line key={b.branch} type="monotone" dataKey={b.branch} stroke={CBS_COLORS[i % CBS_COLORS.length]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
